/* eslint-disable max-lines-per-function */
import { HttpErrorResponse } from '@angular/common/http';

import type { ID } from '@core/ngrx-legacy';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  ngrxEvent,
  pipeState,
  scopeFactory,
} from '@core/ngrx-legacy';
import {
  AutomationTemplate,
  WorkflowAutomation,
  WorkflowAutomationAdditionalData,
  WorkflowAutomationCreatePayload,
  WorkflowAutomationOptionsData,
  WorkflowAutomationUpdatePayload,
} from '@core/workflow-automations-types';

import { createHydratedEntityState } from '../../../../../../../../libs/ngrx/legacy/lib/entity-collection/data';

export const stageIdScope = (stageId: number) => scopeFactory('stageId', stageId);

const idSelector = ({ id }: { id: number }) => id;
const { actions, namespace, reducer, selectors } =
  createEntityCollectionStore<WorkflowAutomation>()('PipelineTemplateStageAutomations', {
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);

      return {
        fetchStages: (state, payload: { pipelineTemplateId: number }) =>
          adapter.markAsPending(state),
        fetchStagesSuccess: (
          state,
          payload: {
            data: Array<{ id: number; workflowAutomations: WorkflowAutomation[] }>;
            pipelineTemplateId: number;
          },
        ) =>
          pipeState(
            state,
            next => adapter.resetAll(next),
            next =>
              createHydratedEntityState(
                next,
                payload.data.reduce(
                  (acc, stageAutomation) => {
                    const scopeId = stageIdScope(stageAutomation.id);
                    acc[scopeId] = stageAutomation.workflowAutomations;
                    return acc;
                  },
                  <Record<string, WorkflowAutomation[]>>{},
                ),
                adapter,
              ),
            next => adapter.markAsLoaded(next, { scope: null, entities: [] }),
          ),
        fetchStagesFailed: defaultActions.fetchCollectionFailed,
        fetchOneStage: (
          state,
          payload: { pipelineTemplateId: number; stageId: number; scope: string },
        ) => adapter.markAsPending(state, { scope: null, entities: [payload.stageId] }),
        fetchOneStageSuccess: (state, { data, scope }) =>
          pipeState(
            state,
            next => adapter.setMany(next, data, { scope }),
            next =>
              adapter.markAsLoaded(next, { scope, entities: data.map(({ id }: { id: ID }) => id) }),
          ),
        fetchOneStageFailed: defaultActions.fetchOneFailed,
        createOnePessimistic: (
          state,
          payload: {
            data: WorkflowAutomationCreatePayload;
            additionalData: WorkflowAutomationAdditionalData;
            scope: string;
            correlationId: string;
          },
        ) => adapter.markAsPending(state),
        updateOnePessimistic: (
          state,
          payload: {
            data: WorkflowAutomationUpdatePayload;
            additionalData: WorkflowAutomationAdditionalData;
            scope: string;
            correlationId: string;
          },
        ) => adapter.markAsPending(state),
        removeOnePessimistic: (
          state,
          payload: { data: number; additionalData: WorkflowAutomationAdditionalData },
        ) => defaultActions.removeOnePessimistic(state, payload),
        duplicate: (
          state,
          payload: {
            data: number;
            additionalData: WorkflowAutomationAdditionalData;
            scope: string;
          },
        ) => adapter.markAsPending(state),
        duplicateSuccess: (state, payload: { data: WorkflowAutomation; scope: string }) =>
          pipeState(
            state,
            next => adapter.addOne(next, payload.data, { scope: payload.scope }),
            next => adapter.markAsLoaded(next),
          ),
        duplicateFailed: defaultActions.createOnePessimisticFailed,
        fetchOptions: ngrxEvent<{ id: number }>(),
        fetchOptionsSuccess: ngrxEvent<WorkflowAutomationOptionsData>(),
        fetchOptionsFailed: ngrxEvent<{ id: number; error: HttpErrorResponse }>(),
        fetchAutomationTemplates: ngrxEvent<{ id: number }>(),
        fetchAutomationTemplatesSuccess: ngrxEvent<AutomationTemplate[]>(),
        fetchAutomationTemplatesFailed: ngrxEvent<{ id: number; error: HttpErrorResponse }>(),
      };
    },
  });

const PipelineTemplateStageAutomationsActions = {
  fetchStages: actions.fetchStages,
  fetchStagesSuccess: actions.fetchStagesSuccess,
  fetchStagesFailed: actions.fetchStagesFailed,

  fetchOneStage: actions.fetchOneStage,
  fetchOneStageSuccess: actions.fetchOneStageSuccess,
  fetchOneStageFailed: actions.fetchOneStageFailed,

  createOnePessimistic: actions.createOnePessimistic,
  createOnePessimisticSuccess: actions.createOnePessimisticSuccess,
  createOnePessimisticFailed: actions.createOnePessimisticFailed,

  updateOnePessimistic: actions.updateOnePessimistic,
  updateOnePessimisticSuccess: actions.updateOnePessimisticSuccess,
  updateOnePessimisticFailed: actions.updateOnePessimisticFailed,

  removeOnePessimistic: actions.removeOnePessimistic,
  removeOnePessimisticFailed: actions.removeOnePessimisticFailed,
  removeOnePessimisticSuccess: actions.removeOnePessimisticSuccess,

  duplicate: actions.duplicate,
  duplicateSuccess: actions.duplicateSuccess,
  duplicateFailed: actions.duplicateFailed,

  fetchOptions: actions.fetchOptions,
  fetchOptionsSuccess: actions.fetchOptionsSuccess,
  fetchOptionsFailed: actions.fetchOptionsFailed,

  fetchAutomationTemplates: actions.fetchAutomationTemplates,
  fetchAutomationTemplatesSuccess: actions.fetchAutomationTemplatesSuccess,
  fetchAutomationTemplatesFailed: actions.fetchAutomationTemplatesFailed,
};

export {
  PipelineTemplateStageAutomationsActions,
  namespace as PipelineTemplateStageAutomationsNamespace,
  reducer as PipelineTemplateStageAutomationsReducer,
  selectors as PipelineTemplateStageAutomationsSelectors,
};
